<template>
  <section ref="elementRoot" class="banner-collections-block custom-section rm-pt">
    <Swiper
      v-if="banners.length"
      :modules="swiperModules"
      :loop="banners.length > 1"
      :autoplay="banners.length > 1 ? { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true } : false"
      :pagination="{ clickable: true }"
      :lazy="{ loadPrevNext: true }"
      @init="onSwiperInit"
      @slide-change="onSlideChange"
    >
      <SwiperSlide
        v-for="(banner, index) in banners"
        :key="`main-slide-${banner.image_id}-${index}`"
        :class="{'swiper-slide-active': index === 0}"
      >
        <div
          class="banner-collections-slider"
          :style="getSlideStyles(banner)"
        >
          <div class="container">
            <div class="banner-collections-slider__content">
              <div
                class="banner-collections-slider__title h1"
                v-html="banner.title"
              />
              <div
                class="banner-collections-slider__message"
                v-html="banner.text"
              />
              <div class="banner-collections-slider__link">
                <NuxtLink :to="banner.link" :prefetch="index === 0">
                  Подробнее
                </NuxtLink>
              </div>
            </div>
          </div>
          <div class="banner-collections-slider__image">
            <template v-if="isInitSwiper || index === 0">
              <NuxtImg
                :src="getImageId(banner)"
                :height="isMobile ? '700' : '640'"
                fit="crop"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :fetchpriority="index === 0 ? 'high' : 'auto'"
                :placeholder="index > 0 ? [50, 25, 75, 5] : undefined"
                decoding="async"
                :alt="banner.title || 'Баннер'"
                format="webp"
                :preload="index <= 1"
                @contextmenu.prevent
              />
            </template>
            <template v-if="isInitSwiper && index > 0">
              <client-only>
                <NuxtImg
                  :src="getImageId(banner)"
                  :height="isMobile ? '700' : '640'"
                  fit="crop"
                  :loading="index === 0 ? 'eager' : 'lazy'"
                  :fetchpriority="index === 0 ? 'high' : 'auto'"
                  :placeholder="index > 0 ? [50, 25, 75, 5] : undefined"
                  decoding="async"
                  :alt="banner.title || 'Баннер'"
                  format="webp"
                  :preload="index <= 1"
                  @contextmenu.prevent
                />
              </client-only>
            </template>
          </div>
        </div>
      </SwiperSlide>

      <ClientOnly>
        <button
          v-if="banners.length > 1"
          class="banner-collections-block__navigation --left"
          type="button"
          aria-label="Предыдущий слайд"
          @click.stop.prevent="prevSlide"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button
          v-if="banners.length > 1"
          class="banner-collections-block__navigation --right"
          type="button"
          aria-label="Следующий слайд"
          @click.stop.prevent="nextSlide"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div
          v-if="isMobile && customPaginationCount.length && banners.length > 1"
          class="banner-collections-block__custom-mobile-pagination"
        >
          <div
            v-for="(indexCount, idx) in customPaginationCount"
            :key="`pagination-${idx}`"
            :class="{ active: indexCount.includes(activeSlide) }"
          />
        </div>
      </ClientOnly>
    </Swiper>
  </section>
</template>

<script lang="ts" setup>
import { Pagination, Autoplay } from 'swiper/modules';
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import { useIntersectionObserver, useThrottleFn } from '@vueuse/core';

const Swiper = defineAsyncComponent(() => import('swiper/vue').then(m => m.Swiper));
const SwiperSlide = defineAsyncComponent(() => import('swiper/vue').then(m => m.SwiperSlide));

const props = defineProps<IBlockDefaultProps>();
const $viewport = useViewport();

const swiperModules = [Pagination, Autoplay];

// Refs
const elementRoot = ref<HTMLDivElement | null>(null);
const refSwiper = ref<any>(null);
const activeSlide = ref<number>(1);
const isUsedZoom = ref<boolean>(false);

// Computed
const isMobile = computed(() => {
  return $viewport.isLessThan('lg');
});

const banners = computed(() => {
  const extras: any = props?.extras;
  return [...(extras?.banners || [])];
});

const customPaginationCount = computed(() => {
  const count = banners.value.length;
  return Array.from({ length: count }, (_, i) => [i + 1]);
});

// Methods
const getImageId = (banner: any): string => {
  const imageId = isMobile.value && banner?.mobile_image_id ? banner.mobile_image_id : banner?.image_id;
  return String(imageId);
};

const getSlideStyles = (banner: any): string => {
  const textColor = banner.color || 'white';
  const opacityPc = (banner.opacity_black_background_percent || 0) / 100;
  const opacityMobile = (banner.opacity_black_background_percent_mobile || 0) / 100;
  return `--text-color: ${textColor}; --opacity-bg-pc: ${opacityPc}; --opacity-bg-mobile: ${opacityMobile}`;
};

const onSlideChange = () => {
  activeSlide.value = (refSwiper.value?.realIndex || 0) + 1;
};

const prevSlide = () => {
  if (!refSwiper.value) return;
  const swiper = refSwiper.value;
  if (!swiper.initialized || swiper.isLocked || !swiper.enabled || swiper.animating || !swiper.allowSlidePrev) {
    return;
  }

  const wasAutoplayRunning = swiper.autoplay?.running;
  if (wasAutoplayRunning) {
    swiper.autoplay.stop();
  }

  const currentRealIndex = swiper.realIndex;
  const realSlidesCount = banners.value.length;

  try {
    if (swiper.params.loop) {
      const targetIndex = currentRealIndex > 0 ? currentRealIndex - 1 : realSlidesCount - 1;
      swiper.slideToLoop(targetIndex, 300);
    } else {
      const targetIndex = Math.max(0, currentRealIndex - 1);
      swiper.slideTo(targetIndex, 300);
    }

    if (wasAutoplayRunning) {
      setTimeout(() => {
        if (refSwiper.value?.autoplay && !refSwiper.value.autoplay.running) {
          refSwiper.value.autoplay.start();
        }
      }, 500);
    }
  } catch (error) {
    swiper.slidePrev();
  }
};

const nextSlide = () => {
  if (!refSwiper.value) return;

  const swiper = refSwiper.value;

  if (!swiper.initialized || swiper.isLocked || !swiper.enabled || swiper.animating || !swiper.allowSlideNext) {
    return;
  }

  const wasAutoplayRunning = swiper.autoplay?.running;
  if (wasAutoplayRunning) {
    swiper.autoplay.stop();
  }

  const currentRealIndex = swiper.realIndex;
  const realSlidesCount = banners.value.length;

  try {
    if (swiper.params.loop) {
      const targetIndex = currentRealIndex < realSlidesCount - 1 ? currentRealIndex + 1 : 0;
      swiper.slideToLoop(targetIndex, 300);
    } else {
      const targetIndex = Math.min(realSlidesCount - 1, currentRealIndex + 1);
      swiper.slideTo(targetIndex, 300);
    }

    // Перезапускаем autoplay через небольшую задержку
    if (wasAutoplayRunning) {
      setTimeout(() => {
        if (refSwiper.value?.autoplay && !refSwiper.value.autoplay.running) {
          refSwiper.value.autoplay.start();
        }
      }, 500);
    }
  } catch (error) {
    // Fallback на стандартный метод
    swiper.slideNext();
  }
};

const isInitSwiper: ComputedRef<boolean> = computed(() => {
  return Boolean(refSwiper.value)
})
const onSwiperInit = (element: any) => {
  refSwiper.value = element;
};

// Throttled zoom check for better performance
const checkUsedZoom = useThrottleFn(() => {
  if (!refSwiper.value) return;

  const currentScale = window.visualViewport?.scale || 1;
  const isZoomed = currentScale > 1.1;

  if (isUsedZoom.value !== isZoomed) {
    isUsedZoom.value = isZoomed;
    if (isZoomed) {
      refSwiper.value.disable();
    } else {
      refSwiper.value.enable();
    }
  }
}, 100);

// Intersection observer for autoplay optimization
let stopObserver: (() => void) | null = null;

onMounted(() => {
  if (process.client) {
    window.addEventListener('touchmove', checkUsedZoom, { passive: true });

    // Initialize intersection observer only on client
    if (elementRoot.value && banners.value.length > 1) {
      const { stop } = useIntersectionObserver(
        elementRoot,
        ([{ isIntersecting }]) => {
          if (refSwiper.value) {
            if (isIntersecting) {
              refSwiper.value.enable();
              refSwiper.value.autoplay?.start();
            } else {
              refSwiper.value.disable();
              refSwiper.value.autoplay?.stop();
            }
          }
        },
        { threshold: 0.1 }
      );
      stopObserver = stop;
    }
  }
});

onBeforeUnmount(() => {
  if (stopObserver) {
    stopObserver();
  }
  if (process.client) {
    window.removeEventListener('touchmove', checkUsedZoom);
  }
});
</script>

<style lang="scss">
.banner-collections-block {
	//margin-top: -40px;
	margin-top: -2.2%;
	position: relative;
	overflow: hidden;
	//contain: layout style paint;

	.swiper {
		border-radius: 0 0 40px 40px;
	}

	.swiper-slide {
		display: flex;
		//contain: layout style paint;

		//&.swiper-slide-visible {
		//	pointer-events: initial !important;
		//}
    //
		//&:not(.swiper-slide-visible) {
		//	opacity: 0 !important;
		//}
	}

	.swiper-pagination {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		width: auto;
		left: initial;
		bottom: 64px;
		right: calc((100vw - var(--width-container)) / 2);

		& > * {
			height: 4px;
			width: 32px;
			border-radius: 100px;
			opacity: 0.5;
			background: #F5F5F5;
			transition: all 0.3s;

			&.swiper-pagination-bullet-active {
				width: 64px;
				opacity: 1;
				background-color: white;
			}
		}
	}

	.h1 {
		color: var(--text-color, white);
	}
}

.banner-collections-block__navigation {
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 50%;
	width: 36px;
	height: 36px;
	z-index: 5;
	cursor: pointer;
	background: transparent;
	border: none;
	padding: 0;
	transform: translateY(-50%);
	transition: opacity 0.2s, transform 0.2s;

	svg {
		width: 28px;
		height: 28px;
		color: white;
		filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
		transition: transform 0.2s;
	}

	&:hover {
		opacity: 0.8;

		svg {
			transform: scale(1.1);
		}
	}

	&:active {
		transform: translateY(-50%) scale(0.95);
	}

	&.--left {
		left: calc((100% - var(--width-container)) / 2 - 36px);
	}

	&.--right {
		right: calc((100% - var(--width-container)) / 2 - 36px);
	}
}

.banner-collections-slider {
	display: flex;
	width: 100%;
	position: relative;
	z-index: 0;

	&::after {
		content: "";
		float: left;
		padding-top: 33.3335%;
	}
  &:before {
    content: "";
    z-index: 1;
    background: black;
    position: absolute;
    pointer-events: none;
    opacity: var(--opacity-bg-pc);
    top: 0; left: 0; bottom: 0; right: 0;
  }
}

.banner-collections-slider__content {
  position: relative;
  z-index: 2;
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 680px;
	padding-top: 128px;
	margin-left: 108px;
	box-sizing: border-box;
}

.banner-collections-slider__image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -1;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
		pointer-events: none;
		user-select: none;
		will-change: transform;
	}

  .__bg-opacity {
    position: absolute;
    z-index: 2;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: black;
    //opacity: var(--opacity-bg-pc);
  }
}

.banner-collections-slider__title {
	margin-bottom: 24px;
	color: var(--text-color, white);
}

.banner-collections-slider__message {
	color: var(--text-color, white);
	font-size: 16px;
	font-weight: 600;
	line-height: 140%;
}

.banner-collections-slider__link {
	display: flex;
	align-items: flex-start;
	margin-top: 64px;

	a {
		color: var(--text-color, white);
		font-size: 16px;
		line-height: 100%;
		font-weight: 600;
		text-transform: uppercase;
		border-bottom: 1px solid;
		transition: all 0.2s;

		&:hover {
			border-color: transparent;
		}
	}
}

.banner-collections-block__custom-mobile-pagination {
	display: none;
	align-items: flex-end;
}

@media (max-width: 1699px) {
	.banner-collections-block .swiper {
		border-radius: 0 0 30px 30px;
	}
	.banner-collections-slider__content {
		padding-top: 137px;
		margin-left: 99px;
	}

	.banner-collections-block__navigation {
		&.--left {
			left: calc((100% - var(--width-container)) / 2);
		}

		&.--right {
			right: calc((100% - var(--width-container)) / 2);
		}
	}
}

@media (max-width: 1359px) {
	.banner-collections-slider__content {
		margin-left: 64px;
	}
}

@media (max-width: 1199px) {
  .banner-collections-block {
    margin-top: -4%;
  }
	.banner-collections-slider__content {
		margin-left: 48px;
    padding-top: 80px;
	}
  .banner-collections-slider__title {
    margin-top: 16px;
  }
}

@media (max-width: 1023px) {
	.banner-collections-slider {
		margin-top: -40px;
		height: calc(100vh - 165px);

		@supports (height: calc(100svh - 165px)) {
			height: calc(100svh - 165px);
		}
    &:before {
      opacity: var(--opacity-bg-mobile);
    }
	}

	.banner-collections-slider__image {
    padding-top: 40px;
	}

	.banner-collections-slider__content {
		margin: 0;
		padding: 88px 0 0;
	}

	.banner-collections-block__navigation {
		top: auto;
		bottom: 32px;
		width: 32px;
		height: 32px;
		background-color: white;
		border-radius: 6px;
		padding: 8px;
		box-sizing: border-box;
		transform: none;

		&:active {
			transform: scale(0.95);
		}

		&.--left {
			left: 20px;
		}

		&.--right {
			left: 64px;
		}

		svg {
			width: 100%;
			height: 100%;
			color: var(--primary-color, #000);
			filter: none;
		}
	}

	.banner-collections-block .swiper-slide {
		&:not(.swiper-slide-prev):not(.swiper-slide-active):not(.swiper-slide-next) {
      .banner-collections-slider__image img {
        display: none;
      }
    }
  }

	.banner-collections-block .swiper-pagination {
		display: none !important;
	}

	.banner-collections-block__custom-mobile-pagination {
		display: flex;
		position: absolute;
		bottom: 32px;
		right: 20px;
		z-index: 5;
		pointer-events: none;
		gap: 8px;

		& > div {
			width: 24px;
			height: 3px;
			background-color: #F5F5F5;
			opacity: 0.5;
			border-radius: 100px;
			transition: all 0.3s;

			&.active {
				width: 48px;
				opacity: 1;
				background-color: white;
			}
		}
	}
}

@media (max-height: 719px) and (max-width: 1023px) {
	.banner-collections-slider {
		height: calc(100vh - 120px);
		@supports (height: calc(100svh - 120px)) {
			height: calc(100svh - 120px);
		}
    @media(orientation: landscape) {
      min-height: calc(100dvh - 40px);
    }
	}
}
</style>
